import React, { useState, useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import * as routes from 'routes';
import { API_ACCOUNT_COMMISSIONS } from 'api/endpoints';

import { MTableToolbar, Column } from '@material-table/core'; //MTableBodyRow

import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box/Box';
import GetAppIcon from '@material-ui/icons/GetApp';
import Grid from '@material-ui/core/Grid';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Theme } from '@material-ui/core/styles/createTheme';
import ExposureIcon from '@material-ui/icons/Exposure';

import { formatAmountValue, searchInStrings, fractionToPercent, compareAmountValues } from 'helpers/helpers';
import MaterialTable from 'components/Table/Table';
import LoadingIndicator from 'components/Loading/LoadingIndicator';
import HedgingIcon from 'components/Icons/HedgingIcon';
import RebalancingOffIcon from 'components/Icons/RebalancingOff';
import RebalancingStrictIcon from 'components/Icons/RebalancingStrict';
import RebalancingTaxOptIcon from 'components/Icons/RebalancingTaxOpt';
import Info from 'components/Tooltip/Info';
import AccessNone from 'components/Icons/AccessNoneIcon';
import AccessFull from 'components/Icons/AccessFullIcon';
import FilterIcon from 'components/Icons/FilterIcon';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RefreshIcon from '@material-ui/icons/Refresh';
import ErrorIcon from '@material-ui/icons/Error';
import NoteIcon from 'components/Icons/Note';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';

import { EdwardColors, AccountMode, Order } from 'enums';
import { AccountPageTypes, AccountsStore, ClientAccess } from 'stores/accountsStore';
import { RootStore } from 'stores/rootStore';
import compose from 'helpers/compose';
import GenerationDialog from 'components/GenerationDialog/GenerationDialog';
import useStyles, { centerCellStyles, rightCellStyles } from './styles/Accounts';
import Commissions, { CommissionData } from './Commissions/Commissions';
import ExpectedDeposits from './ExpectedDeposits/ExpectedDeposits';
import { Widths } from './Accounts';

interface Props {
  newAccountsStore: AccountsStore;
  rootStore?: RootStore;
}

const ActiveAccounts: React.FC<Props> = ({ newAccountsStore, rootStore }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const {
    actives,
    pageSize,
    onChangeRowsPerPage,
    downloadAccounts,
    accountsState,
    onTableSearchChange,
    onTableOrderChange,
    sortColumns,
    reloadAccounts,
    reloadingActive,
    onColumnDragged,
    onChangeColumnHidden,
  } = newAccountsStore;
  const state = accountsState[AccountPageTypes.ACTIVE];

  const [filtersVisible, setFilterVisible] = useState(false);
  const [commissionsOpen, setCommissionsOpen] = useState(false);
  const [commission, setCommission] = useState<CommissionData | null>(null);
  const [defaultView, setDefaultView] = useState(!newAccountsStore.accountsState[AccountPageTypes.ACTIVE].cols);
  const [expectedDepositsId, setExpectedDepositsId] = useState<string | null>(null);

  useEffect(() => {
    rootStore!.stores.uiStore.setAccountsListData({ newAccountsStore, tab: AccountPageTypes.ACTIVE });
    return () => {
      rootStore!.stores.uiStore.unsetAccountsListData();
    };
  });

  const userModified = newAccountsStore.accountsState[AccountPageTypes.ACTIVE].userModified;

  useEffect(() => {
    setDefaultView(!newAccountsStore.accountsState[AccountPageTypes.ACTIVE].userModified);
  }, [newAccountsStore.accountsState, userModified]);

  const toggleFilterVisibility = () => {
    setFilterVisible(!filtersVisible);
  };

  const tablet = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'), { noSsr: true }); //@media (min-width:640px)
  const mid = useMediaQuery('(min-width:685px)', { noSsr: true });
  const laptop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'), { noSsr: true });
  const desktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'), { noSsr: true });
  const width = desktop
    ? Widths.DESKTOP
    : laptop
    ? Widths.LAPTOP
    : mid
    ? Widths.MID
    : tablet
    ? Widths.TABLET
    : Widths.MOBILE;

  if (!actives) {
    return (
      <div className={classes.loadingContainer}>
        <LoadingIndicator size={56} />
      </div>
    );
  }

  const isActiveTextHelper = (variable: any) => (variable ? t('general_is_active') : t('general_isnot_active'));

  const renderPortfolio = (portfolio: any) => {
    if (!portfolio) return;
    const { rebalancing, rebalancingTimeTest, currencyHedging } = portfolio;
    const rebalancingMode = rebalancing
      ? rebalancingTimeTest || rebalancingTimeTest === undefined
        ? 'accountDetail_plan_rebalancing_taxOpt'
        : 'accountDetail_plan_rebalancing_strict'
      : undefined;

    return (
      <Box display="flex" alignItems="center" justifyContent="center">
        <span>{portfolio.portfolio}</span>
        {'   '}
        <Tooltip
          title={`${t('accountDetail_plan_rebalancing')} ${
            rebalancingMode ? t(rebalancingMode) : isActiveTextHelper(rebalancing)
          }`}
          placement="top"
        >
          <Box display="inline-flex" component="span">
            {rebalancing && (rebalancingTimeTest || rebalancingTimeTest === undefined) ? (
              <RebalancingTaxOptIcon transparentBgr defaultStroke fontSize="medium" style={{ width: '1.3em', height: '1.3em' }} />
            ) : rebalancing && rebalancingTimeTest === false ? (
              <RebalancingStrictIcon transparentBgr fontSize="medium" />
            ) : (
              <RebalancingOffIcon transparentBgr fontSize="medium" htmlColor="#ccc" />
            )}
          </Box>
        </Tooltip>
        {'   '}
        <Tooltip
          title={`${t('accountDetail_plan_currencyHedging')} ${isActiveTextHelper(currencyHedging)}`}
          placement="top"
        >
          <Box display="inline-flex" component="span">
            {currencyHedging ? (
              <HedgingIcon transparentBgr fontSize="medium" htmlColor={EdwardColors.GREEN} />
            ) : (
              <HedgingIcon transparentBgr fontSize="medium" htmlColor="#ccc" />
            )}
          </Box>
        </Tooltip>
      </Box>
    );
  };

  return (
    <>
      <MaterialTable
        enhanced
        refresh
        data={actives}
        components={{
          Toolbar: (props) => (
            <Grid container>
              <MTableToolbar {...props} />
              <div>
                <Tooltip title={t('general_resetTableCols') || ''} placement="top">
                  <div>
                    <IconButton
                      size="small"
                      onClick={() => {
                        newAccountsStore.resetColumns(AccountPageTypes.ACTIVE);
                        setDefaultView(true);
                      }}
                    >
                      <RotateLeftIcon fontSize="large" />
                    </IconButton>
                  </div>
                </Tooltip>
                <Tooltip title={t('general_show_filters') || ''} placement="top">
                  <div>
                    <IconButton size="small" onClick={toggleFilterVisibility}>
                      <FilterIcon transparentBgr defaultFill fontSize="large" strokeWidth={0.1} />
                    </IconButton>
                  </div>
                </Tooltip>
                <Tooltip title={t('accounts_reload') || ''} placement="top">
                  <div>
                    <IconButton size="small" onClick={() => reloadAccounts('ACTIVE')}>
                      {reloadingActive ? <LoadingIndicator /> : <RefreshIcon fontSize="large" />}
                    </IconButton>
                  </div>
                </Tooltip>
                <Button variant="outlined" onClick={downloadAccounts} startIcon={<GetAppIcon />}>
                  {t('accounts_download_csv_short')}
                </Button>
              </div>
            </Grid>
          ),
        }}
        columns={sortColumns(
          defaultView,
          AccountPageTypes.ACTIVE,
          [
            {
              hiddenByColumnsButton: true,
              title: t('general_account'),
              field: 'clientName',
              cellStyle: width < Widths.DESKTOP ? undefined : { width: '25%' },
              customFilterAndSearch: (filter, rowData, def) =>
                searchInStrings(filter, [rowData.clientName, rowData.accountName]),
              render: ({ clientName, accountName }) => (
                <>
                  <span className={classes.name}>{clientName}</span>
                  <span className={classes.secondLine}>({accountName})</span>
                </>
              ),
              customSort: (a, b) => {
                return `${a.clientName} ${a.accountName}`.localeCompare(`${b.clientName} ${b.accountName}`);
              },
            },
            {
              title: t('accounts_totalAum'),
              field: 'totalAum',
              cellStyle: { ...rightCellStyles },
              headerStyle: { textAlign: 'right' },
              render: ({ totalAum, currency }) => formatAmountValue(totalAum, currency),
              customSort: (a, b) =>
                compareAmountValues(
                  { value: a.totalAum, currency: a.currency },
                  { value: b.totalAum, currency: b.currency },
                  newAccountsStore?.summary?.conversionRate,
                ),
            },
            {
              title: (
                <>
                  {t('accounts_totalProfit')}
                  <Info content={t('tooltip_accounts_totalProfit')} inline />
                </>
              ),
              field: 'totalProfit',
              cellStyle: { ...rightCellStyles },
              headerStyle: { textAlign: 'right' },
              render: ({ totalProfit, yearlyProfit, currency }) => (
                <>
                  <span className={`${classes.profit} ${totalProfit && `negative_${totalProfit < 0}`}`}>
                    {formatAmountValue(totalProfit, currency)}
                  </span>
                  {yearlyProfit !== null && (
                    <>
                      <br />
                      {` (${fractionToPercent(yearlyProfit, 2)} p. a.)`}
                    </>
                  )}
                </>
              ),
              customSort: (a, b) =>
                compareAmountValues(
                  { value: a.totalProfit, currency: a.currency },
                  { value: b.totalProfit, currency: b.currency },
                  newAccountsStore?.summary?.conversionRate,
                ),
            },
            {
              hidden: defaultView && width < Widths.DESKTOP,
              field: 'expectedDeposits',
              title: (
                <>
                  {t('accounts_expectedDeposits')}
                  <Info content={t('tooltip_accounts_expectedDeposits')} inline />
                </>
              ),
              cellStyle: { ...rightCellStyles },
              headerStyle: { textAlign: 'right' },
              render: ({ expectedDeposits, currency, accountId }) => (
                <Tooltip title={t('tooltip_accounts_expectedDepositsLink') || ''} placement="top">
                  <span
                    className={`${classes.balance} ${
                      expectedDeposits !== 0 ? 'negative_' + (expectedDeposits < 0) : ''
                    }`}
                    style={{ textDecoration: 'underline' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpectedDepositsId(accountId);
                    }}
                  >
                    {expectedDeposits === 0 ? t('general_OK') : formatAmountValue(expectedDeposits, currency, true)}
                  </span>
                </Tooltip>
              ),
              customSort: (a, b) =>
                compareAmountValues(
                  { value: a.expectedDeposits, currency: a.currency },
                  { value: b.expectedDeposits, currency: b.currency },
                  newAccountsStore?.summary?.conversionRate,
                ),
            },
            {
              hidden: defaultView && width < Widths.DESKTOP,
              title: t('accounts_monthInvestment'),
              field: 'regularInvestment',
              cellStyle: { ...rightCellStyles },
              headerStyle: { textAlign: 'right' },
              render: ({ regularInvestment, currency }) => formatAmountValue(regularInvestment, currency),
              customSort: (a, b) =>
                compareAmountValues(
                  { value: a.regularInvestment, currency: a.currency },
                  { value: b.regularInvestment, currency: b.currency },
                  newAccountsStore?.summary?.conversionRate,
                ),
            },
            {
              hidden: defaultView && width < Widths.LAPTOP,
              title: t('bucket.long_term.title'),
              field: 'portfolioLongBalance',
              cellStyle: { ...centerCellStyles },
              headerStyle: { textAlign: 'center' },
              customFilterAndSearch: (filter, rowData, def) =>
                searchInStrings(filter, [rowData.portfolioLongBalance, rowData.portfolioLong.portfolio]),
              render: ({ portfolioLongBalance, portfolioLongInfo, currency, portfolioLong, ...rest }) => {
                return (
                  <>
                    {formatAmountValue(portfolioLongBalance, currency)}
                    <span className={classes.secondLine}>{renderPortfolio(portfolioLong)}</span>
                  </>
                );
              },
              customSort: (a, b) =>
                compareAmountValues(
                  { value: a.portfolioLongBalance, currency: a.currency },
                  { value: b.portfolioLongBalance, currency: b.currency },
                  newAccountsStore?.summary?.conversionRate,
                ),
            },
            {
              hidden: defaultView && width < Widths.LAPTOP,
              title: t('bucket.mid_term.title'),
              field: 'portfolioMidBalance',
              cellStyle: { ...centerCellStyles },
              headerStyle: { textAlign: 'center' },
              customFilterAndSearch: (filter, rowData, def) =>
                searchInStrings(filter, [rowData.portfolioMidBalance, rowData.portfolioMid.portfolio]),
              render: ({ portfolioMidBalance, portfolioMid, currency }) => (
                <>
                  {formatAmountValue(portfolioMidBalance, currency)}
                  <span className={classes.secondLine}>{renderPortfolio(portfolioMid)}</span>
                </>
              ),
              customSort: (a, b) =>
                compareAmountValues(
                  { value: a.portfolioMidBalance, currency: a.currency },
                  { value: b.portfolioMidBalance, currency: b.currency },
                  newAccountsStore?.summary?.conversionRate,
                ),
            },
            {
              hidden: defaultView && width < Widths.LAPTOP,
              title: t('bucket.short_term.title'),
              field: 'portfolioShortBalance',
              cellStyle: { ...centerCellStyles },
              headerStyle: { textAlign: 'center' },
              customFilterAndSearch: (filter, rowData, def) =>
                searchInStrings(filter, [rowData.portfolioShortBalance, rowData.portfolioShort.portfolio]),
              render: ({ portfolioShortBalance, portfolioShort, currency }) => (
                <>
                  {formatAmountValue(portfolioShortBalance, currency)}
                  <span className={classes.secondLine}>{renderPortfolio(portfolioShort)}</span>
                </>
              ),
              customSort: (a, b) =>
                compareAmountValues(
                  { value: a.portfolioShortBalance, currency: a.currency },
                  { value: b.portfolioShortBalance, currency: b.currency },
                  newAccountsStore?.summary?.conversionRate,
                ),
            },
            {
              hidden: defaultView && width < Widths.TABLET,
              title: t('accounts_portfolioWallet'),
              field: 'portfolioWallet',
              cellStyle: { ...rightCellStyles },
              headerStyle: { textAlign: 'right' },
              render: ({ portfolioWallet, currency }) => formatAmountValue(portfolioWallet, currency),
              customSort: (a, b) =>
                compareAmountValues(
                  { value: a.portfolioWallet, currency: a.currency },
                  { value: b.portfolioWallet, currency: b.currency },
                  newAccountsStore?.summary?.conversionRate,
                ),
            },
            {
              hidden: defaultView && width < Widths.TABLET,
              title: t('accounts_mode'),
              field: 'planMode',
              cellStyle: { ...centerCellStyles },
              headerStyle: { textAlign: 'center' },
              render: ({ portfolioOnly, planMode }) =>
                planMode === AccountMode.LTIP
                  ? t('variantSelection_LIP')
                  : portfolioOnly
                  ? t('variantSelection_portfolioOnly')
                  : t('variantSelection_withInvestment_alt'),
              customSort: (a, b) => {
                const mode = a.planMode.localeCompare(b.planMode);
                const po = a.portfolioOnly === b.portfolioOnly ? 0 : a.portfolioOnly ? -1 : 1;
                return mode === 0 ? po : mode;
              },
              customFilterAndSearch: (filter, rowData, def) => {
                const value =
                  rowData.planMode === AccountMode.LTIP
                    ? t('variantSelection_LIP')
                    : rowData.portfolioOnly
                    ? t('variantSelection_portfolioOnly')
                    : t('variantSelection_withInvestment_alt');
                return searchInStrings(filter, [value]);
              },
            },
            {
              hidden: defaultView && width < Widths.TABLET,
              title: (
                <>
                  Typ služby
                  <Info content={'Služba obhospodařování, nebo přijímání a předávání pokynů'} inline />
                </>
              ),
              field: 'clientTaxMode',
              cellStyle: { ...centerCellStyles },
              headerStyle: { textAlign: 'center' },
            },
            {
              hidden: defaultView && width < Widths.TABLET,
              title: t('accounts_clientView'),
              field: 'clientView',
              cellStyle: { ...centerCellStyles },
              headerStyle: { textAlign: 'center' },
              render: ({ clientAccess }) => {
                return (
                  <Tooltip title={t(`accounts_access_${clientAccess}`) || ''} placement="top">
                    <span>
                      {clientAccess === ClientAccess.SECOND_FACTOR_MISSING && (
                        <ErrorIcon htmlColor={EdwardColors.DARK} />
                      )}
                      {clientAccess === ClientAccess.ACCESS_NOT_ACTIVE && (
                        <AccessNone htmlColor="#999" defaultFill transparentBgr />
                      )}
                      {clientAccess === ClientAccess.FULL_ACCESS && (
                        <AccessFull htmlColor="#000" defaultFill transparentBgr strokeWidth={0} />
                      )}
                      {clientAccess === ClientAccess.SINGLE_FACTOR_ONLY && <CheckCircleIcon htmlColor="#999" />}
                      {clientAccess === ClientAccess.BASIC_ACCESS && <CheckCircleIcon />}
                    </span>
                  </Tooltip>
                );
              },
            },
            {
              hidden: !newAccountsStore.activeCommissions,
              hiddenByColumnsButton: true,
              title: t('accounts_commissions'),
              lookup: {
                true: t('general_yes'),
                false: t('general_no'),
              },
              field: 'commissions',
              render: (account) =>
                account.hasCommission ? (
                  <Tooltip title={t('accounts_commissions') || ''} placement="top">
                    <IconButton
                      size="medium"
                      onClick={async (event) => {
                        event.stopPropagation();
                        rootStore!.stores.uiStore.setLoading(true);
                        const commissionData = await rootStore!.stores.apiStore.get<CommissionData>(
                          API_ACCOUNT_COMMISSIONS(account.accountId),
                        );
                        rootStore!.stores.uiStore.setLoading(false);
                        if (commissionData?.data) {
                          setCommission(commissionData.data);
                          setCommissionsOpen(true);
                        }
                      }}
                    >
                      <ExposureIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                ) : null,
            },
            {
              hidden: defaultView && width < Widths.MID,
              hiddenByColumnsButton: true,
              cellStyle: {
                width: width > Widths.TABLET ? '10%' : '5%',
              },
              field: 'actions',
              render: (account) => (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}
                >
                  <Tooltip
                    title={t(account.hasNotes ? 'accountNotes_title' : 'accountNotes_add') || ''}
                    placement="top"
                  >
                    <IconButton
                      size="medium"
                      onClick={(event) => {
                        event.stopPropagation();
                        rootStore!.stores.uiStore.openNotes(account.accountId);
                      }}
                    >
                      {account.hasNotes ? (
                        <NoteIcon
                          fontSize="small"
                          htmlColor={EdwardColors.SECONDARY}
                          defaultFill
                          transparentBgr
                          strokeWidth={0}
                        />
                      ) : (
                        <NoteIcon
                          fontSize="small"
                          htmlColor={EdwardColors.GRAY_DARKER}
                          defaultFill
                          transparentBgr
                          strokeWidth={0}
                        />
                      )}
                    </IconButton>
                  </Tooltip>
                </div>
              ),
            },
          ],
          Order.DESC,
          1,
        )}
        options={{
          searchFieldAlignment: 'left',
          headerStyle: {
            borderBottom: '2px solid rgb(224, 224, 224)',
          },
          pageSize: Math.min(pageSize, 50), //EDS-3894
          filtering: filtersVisible,
          sorting: true,
          searchText: state.search,
          columnsButton: true,
        }}
        onSearchChange={(search) => onTableSearchChange(AccountPageTypes.ACTIVE, search)}
        onOrderChange={(orderedColumnId, orderDirection) =>
          onTableOrderChange(AccountPageTypes.ACTIVE, orderedColumnId, orderDirection as Order)
        }
        onRowClick={(_event, { accountId }) => {
          history.push(routes.ACCOUNTS_DETAIL(accountId));
        }}
        onRowsPerPageChange={onChangeRowsPerPage}
        onColumnDragged={(sourceIndex: number, destinationIndex: number) =>
          onColumnDragged(AccountPageTypes.ACTIVE, sourceIndex, destinationIndex)
        }
        onChangeColumnHidden={(column: Column<any>, hidden: boolean) =>
          onChangeColumnHidden(AccountPageTypes.ACTIVE, column.field, hidden, true)
        }
      />
      <GenerationDialog store={newAccountsStore} />

      {commissionsOpen && commission && (
        <Commissions
          handleClose={setCommissionsOpen}
          commissionsToken={commission.token}
          clientName={commission.client}
          commissionId={commission.commissionId}
          contractId={commission.contractNumber}
        />
      )}

      {expectedDepositsId && (
        <ExpectedDeposits
          accountId={expectedDepositsId}
          handleClose={() => setExpectedDepositsId(null)}
          handleRefresh={() => reloadAccounts('ACTIVE')}
        />
      )}
    </>
  );
};
export default compose(inject('newAccountsStore', 'rootStore'), observer)(ActiveAccounts);
